import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, ArrowRight } from "lucide-react";
import { getReasonCodes, type ReasonPayload, type ReasonScene } from "@/services/reasonCodes";

/**
 * REASON-1 · 通用「填写原因」弹窗（从 OrderCancelDialog 的「取消原因」抽出来，价格 / 授信 / 上下架等关键操作共用）。
 *
 * - 快选按钮取自后端 `GET /api/v1/reason-codes?scene=`（唯一真源 services/reason_codes.py），前端不写死；
 * - 选「其它」类代码 → 补充说明必填；其它代码补充说明选填；
 * - `changes` 给「改前 → 改后」小表（价格类页面必给）；
 * - `required=false`（如上架）时多一个「不填，直接继续」。
 */
export interface ReasonChange { label: string; before: string; after: string }

export interface ReasonDialogProps {
  scene: ReasonScene;
  title: string;
  /** 一句话说明正在做什么，如「调整产品价格」 */
  subtitle?: string;
  changes?: ReasonChange[];
  /** 覆盖场景默认的必填性（上架：选填） */
  required?: boolean;
  /** PRICE-CUST-1 · 补充说明必填（价格低于成本：不能只选原因代码）；红字提示 */
  textRequired?: boolean;
  textRequiredHint?: string;
  /** 只显示某一组快选（临时额度审批：approve / reject） */
  group?: "approve" | "reject";
  confirmText?: string;
  danger?: boolean;
  onConfirm: (r: ReasonPayload) => void;
  onCancel: () => void;
}

export function ReasonDialog({ scene, title, subtitle, changes, required, textRequired, textRequiredHint, group, confirmText = "确认", danger, onConfirm, onCancel }: ReasonDialogProps) {
  const q = useQuery({ queryKey: ["reason-codes", scene], queryFn: () => getReasonCodes(scene), staleTime: 5 * 60_000 });
  const [code, setCode] = useState<string>("");
  const [text, setText] = useState("");
  const items = (q.data?.items ?? []).filter((i) => !group || i.group === group);
  const mustFill = required ?? q.data?.required ?? true;
  const picked = items.find((i) => i.code === code);
  const textNeeded = !!picked?.needs_text || !!textRequired;
  const textMax = q.data?.text_max ?? 500;
  // 必填：要选一个代码；选「其它」还要写说明。选填：什么都不选也可以确认
  const ok = (mustFill ? !!picked && (!textNeeded || !!text.trim()) : !picked || !textNeeded || !!text.trim()) && (!textRequired || !!text.trim());

  const submit = () => {
    if (!ok) return;
    onConfirm({ ...(picked ? { reason_code: picked.code } : {}), ...(text.trim() ? { reason: text.trim() } : {}) });
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 backdrop-blur-sm animate-in fade-in" role="dialog" aria-modal="true" data-testid="reason-dialog">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white p-6 shadow-xl animate-in zoom-in-95 slide-in-from-bottom-4">
        <div className="mb-4 flex items-center gap-3">
          <div className={`flex h-10 w-10 items-center justify-center rounded-full ${danger ? "bg-red-100" : "bg-blue-50"}`}>
            <AlertTriangle size={20} className={danger ? "text-red-500" : "text-blue-600"} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-slate-900">{title}</h2>
            {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
          </div>
        </div>

        {changes && changes.length > 0 && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="reason-changes">
            {changes.map((c) => (
              <div key={c.label} className="flex items-center justify-between gap-2 py-0.5 text-[13px]">
                <span className="text-slate-600">{c.label}</span>
                <span className="inline-flex items-center gap-1.5 font-medium text-slate-900">
                  <span className="text-slate-400 line-through">{c.before}</span>
                  <ArrowRight size={13} className="text-slate-400" />
                  <span>{c.after}</span>
                </span>
              </div>
            ))}
          </div>
        )}

        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          原因 {mustFill && <span className="text-red-500">*</span>}
        </label>
        {q.isLoading && <p className="text-[12px] text-slate-400">加载中…</p>}
        {q.isError && <p className="text-[12px] text-rose-600">原因选项加载失败，请关闭后重试</p>}
        <div className="mb-3 flex flex-wrap gap-2" data-testid="reason-chips">
          {items.map((i) => (
            <button
              key={i.code}
              type="button"
              data-testid={`reason-chip-${i.code}`}
              onClick={() => setCode(code === i.code && !mustFill ? "" : i.code)}
              className={`rounded-full border px-3 py-1 text-[13px] transition ${
                code === i.code ? "border-blue-600 bg-blue-50 font-medium text-blue-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {i.label}
            </button>
          ))}
        </div>

        <label className="mb-1.5 block text-sm font-medium text-slate-700">
          补充说明 {textNeeded ? <span className="text-red-500">*（{textRequired ? textRequiredHint ?? "必须写明" : "选「其它」必须写明"}）</span> : <span className="text-slate-400">（选填）</span>}
        </label>
        <textarea
          rows={3}
          maxLength={textMax}
          data-testid="reason-text"
          className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          placeholder={textNeeded ? "请写明具体原因" : "补充一句，方便以后查（可不写）"}
          value={text}
          onChange={(e) => setText(e.target.value)}
        />

        <div className="mt-5 flex gap-3">
          <button type="button" onClick={onCancel} data-testid="reason-cancel" className="flex-1 rounded-md border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-50">
            返回
          </button>
          <button
            type="button"
            disabled={!ok}
            onClick={submit}
            data-testid="reason-confirm"
            className={`flex-1 rounded-md px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${danger ? "bg-red-600 hover:bg-red-700" : "bg-blue-600 hover:bg-blue-700"}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}

/** 各页面调用：`const { ask, dialog } = useReasonDialog();` → `const r = await ask({...})`（null = 点了「返回」），页面里渲染 `{dialog}`。 */
export class ReasonCancelled extends Error {
  constructor() { super("已取消"); this.name = "ReasonCancelled"; }
}
export const isReasonCancelled = (e: unknown): boolean => e instanceof ReasonCancelled;

type AskOpts = Omit<ReasonDialogProps, "onConfirm" | "onCancel">;

export function useReasonDialog(): { ask: (o: AskOpts) => Promise<ReasonPayload | null>; dialog: ReactNode } {
  const [opts, setOpts] = useState<AskOpts | null>(null);
  const resolver = useRef<((r: ReasonPayload | null) => void) | null>(null);
  useEffect(() => () => { resolver.current?.(null); resolver.current = null; }, []);
  const ask = useCallback((o: AskOpts) => new Promise<ReasonPayload | null>((resolve) => {
    resolver.current = resolve;
    setOpts(o);
  }), []);
  const done = (r: ReasonPayload | null) => { const f = resolver.current; resolver.current = null; setOpts(null); f?.(r); };
  const dialog = opts ? <ReasonDialog {...opts} onConfirm={(r) => done(r)} onCancel={() => done(null)} /> : null;
  return { ask, dialog };
}
